import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent, useEffect, useMemo } from 'react';
import { FieldError, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Larger } from '@/components/Forms';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Switch from '@/components/Switch';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import useDidUpdate from '@/hooks/useDidUpdate';
import useUser from '@/hooks/useUser';
import {
  PermissionResource,
  Role,
  RoleResource,
  TeamResource,
  UserResourceV2,
} from '@/types/resources';
import { api } from '@/utils/api';
import { productionTeams } from '@/utils/data';
import { getInitialsFromString, handleFormErrors } from '@/utils/helpers';

const AllTeams = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 18px 14px;
  display: flex;
`;

const AllProductionTeams = styled(AllTeams)`
  align-items: center;
`;

const StyledSwitch = styled((props) => <Switch<FormDataT> {...props} />)`
  margin-left: 20px;
`;

const Teams = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 8px;
  margin-top: 16px;
`;

const Team = styled.label`
  padding: 7px 8px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  min-height: 48px;
  cursor: pointer;
  transition: all ease-out 0.2s;
  img {
    width: 32px;
    height: 32px;
    border-radius: 4px;
  }
`;

const TeamInitials = styled(Text)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  object-fit: cover;
  margin-right: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscale};
`;

const Permission = styled(Team)`
  justify-content: center;
  position: relative;
  text-align: center;
  ${Text} {
    text-transform: capitalize;
  }
`;

const Checkbox = styled.input`
  display: none;
  &:checked + label {
    box-shadow: 0px 0px 5px rgba(65, 54, 207, 0.15);
    border-color: ${({ theme }) => theme.colors.purple};
  }
  &:disabled + label {
    cursor: not-allowed;
  }
`;

const AllTeamsHeader = styled(Text)`
  margin-bottom: 4px;
`;

const StyledText = styled(Text)`
  margin-bottom: 4px;
`;

export type FormDataT = {
  name: string;
  email: string;
  role: Role;
  assign_to_all_teams: boolean;
  assign_to_all_production_teams: boolean;
  teams: string[];
  permissions: string[];
  production_teams: string[];
  // number[] (raw ids) rather than string[] like `teams` — that's what
  // /v2/users accepts for `companies`.
  companies: number[];
  departments: number[];
};

interface Props {
  user?: UserResourceV2;
  onClose: () => void;
}

const UserModal: FC<Props> = ({ user, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const { data } = useQuery(['roles_teams_permissions'], () =>
    api.get<
      never,
      { roles: RoleResource[]; teams: TeamResource[]; permissions: string[] }
    >('/v2/roles-teams-permissions'),
  );

  const {
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: user?.name ?? '',
      email: user?.email ?? '',
      role: user?.role?.name ?? 'Team Member',
      teams: user?.teams?.map((t) => t.id.toString()) ?? [],
      permissions: user?.permissions ?? [],
      assign_to_all_teams: user?.assignToAllTeams ?? false,
      production_teams: user?.productionTeams ?? [],
      assign_to_all_production_teams: false,
      companies: user?.companies?.map((p) => p.id) ?? [],
      departments: user?.departments?.map((d) => d.id) ?? [],
    },
  });

  const assignToAllTeams = useWatch({ control, name: 'assign_to_all_teams' });
  const selectedTeams = useWatch({ control, name: 'teams' });
  const selectedPermissions = useWatch({ control, name: 'permissions' });
  const selectedProductionTeams = useWatch({
    control,
    name: 'production_teams',
  });

  const assignToAllProductionTeams = useWatch({
    control,
    name: 'assign_to_all_production_teams',
  });

  const currentUser = useUser();

  const role = useWatch({ control, name: 'role' });

  const shouldAssignToAll = role === 'Super Admin' && !user;

  const { data: permissionsList } = useQuery(['permissions'], () =>
    api.get<never, PermissionResource[]>('/v1/permissions'),
  );

  const { data: companyOptions } = useCompaniesOptions();

  const visibleCompanyOptions = useMemo(
    () =>
      (companyOptions ?? [])
        .filter((option) => !option.hide_from_dropdowns)
        .map(({ value, label }) => ({ value, label })),
    [companyOptions],
  );

  const { data: departmentOptions } = useDepartmentsOptions();

  const visibleDepartmentOptions = useMemo(
    () =>
      (departmentOptions ?? [])
        // Keep hidden departments the user already has, otherwise they'd
        // silently drop out of the selection.
        .filter(
          (option) =>
            !option.hide_from_dropdowns ||
            user?.departments?.some((d) => d.id === option.value),
        )
        .map(({ value, label, companyNames }) => ({
          value,
          label: companyNames ? `${label} · ${companyNames}` : label,
        })),
    [departmentOptions, user],
  );

  const resolvedRole = data?.roles.find((r) => r.name === role);

  const rolePermissionNames = resolvedRole?.permissions ?? [];

  const showPermissionsField = role !== 'Super Admin';

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ production_teams, permissions, ...data }) => {
      const allPermissions = Array.from(
        new Set([...(resolvedRole?.permissions ?? []), ...permissions]),
      );

      try {
        if (user) {
          // GET /v2/users returns `companies`, but keep the guard: if the
          // field is ever absent, defaultValues.companies falls back to `[]`
          // and sending that would silently wipe a user's assignments on
          // every Update. An explicit `[]` (the admin actually cleared the
          // field) is still sent. Same for `departments`.
          const { companies, departments, ...rest } = data;

          await api.put(`/v2/users/${user.id}`, {
            ...rest,
            ...(user.companies !== undefined ? { companies } : {}),
            ...(user.departments !== undefined ? { departments } : {}),
            permissions: allPermissions,
            production_teams: data.assign_to_all_production_teams
              ? productionTeams
              : production_teams,
          });
        } else {
          await api.post('/v2/users', {
            ...data,
            permissions: allPermissions,
            production_teams: data.assign_to_all_production_teams
              ? productionTeams
              : production_teams,
          });
        }

        await queryClient.invalidateQueries(['users_v2']);
        alerts.success(
          user ? 'User updated successfully' : 'New user successfully created',
        );
        onClose();
      } catch (error) {
        handleFormErrors<FormDataT>(error, setError, alerts);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  useDidUpdate(() => {
    if (shouldAssignToAll) {
      setValue('assign_to_all_teams', true);
      setValue('assign_to_all_production_teams', true);
    }
  }, [shouldAssignToAll]);

  useEffect(() => {
    const rolePermissions = resolvedRole?.permissions ?? [];

    const userPermissions = user?.permissions ?? [];

    const permissions = Array.from(
      new Set(rolePermissions.concat(userPermissions)),
    );

    setValue('permissions', permissions);
  }, [resolvedRole, user, setValue]);

  return (
    <Modal>
      <SidePanel heading={`${user ? 'Edit' : 'Invite'} user`} onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              {!!user && (
                <FormField<FormDataT>
                  control={control}
                  name="name"
                  placeholder="Add name"
                  label="Name"
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="email"
                placeholder="Add email"
                label="Email"
                disabled={!!user}
              />
              <FormField<FormDataT, Role>
                control={control}
                name="role"
                placeholder="Select role"
                label="Role"
                type="select"
                options={
                  data?.roles?.map((role) => ({
                    value: role.name,
                    label: role.name,
                  })) ?? []
                }
                disabled={user?.id === currentUser?.id}
              />
              <Larger>
                <FormField<FormDataT, number>
                  label="Companies"
                  control={control}
                  name="companies"
                  type="multiselect"
                  placeholder="Add company"
                  options={visibleCompanyOptions}
                />
              </Larger>
              <Larger>
                <FormField<FormDataT, number>
                  label="Departments"
                  control={control}
                  name="departments"
                  type="multiselect"
                  placeholder="Add department"
                  options={visibleDepartmentOptions}
                />
              </Larger>
              <Larger>
                <AllTeams>
                  <div>
                    <AllTeamsHeader $type="smallBold">
                      Assign to all teams
                    </AllTeamsHeader>
                    <Text $color="gray" $type="smallMedium">
                      By assigning users to all current teams, you also provide
                      the access for any future teams.
                    </Text>
                  </div>
                  <div>
                    <StyledSwitch
                      name="assign_to_all_teams"
                      control={control}
                    />
                  </div>
                </AllTeams>
              </Larger>
              {!assignToAllTeams && (
                <Larger>
                  <StyledText $type="smallBold">Teams</StyledText>
                  <Text $color="gray" $type="smallMedium">
                    Select multiple teams for one user.
                  </Text>
                  <Teams>
                    {data?.teams.map((team) => (
                      <div key={team.id}>
                        <Checkbox
                          type="checkbox"
                          id={`team-${team.id}-checkbox`}
                          checked={selectedTeams.includes(team.id.toString())}
                          onChange={(e) => {
                            const id = team.id.toString();
                            setValue(
                              'teams',
                              e.target.checked
                                ? [...selectedTeams, id]
                                : selectedTeams.filter((t) => t !== id),
                            );
                          }}
                        />
                        <Team htmlFor={`team-${team.id}-checkbox`}>
                          {team.icon ? (
                            <TeamInitials
                              as="img"
                              src={
                                team.icon!.versions.icon ||
                                team.icon!.versions.original
                              }
                              alt={team.name}
                            />
                          ) : (
                            <TeamInitials as="span" $type="smallBold">
                              {getInitialsFromString(team.name)}
                            </TeamInitials>
                          )}
                          <Text $type="smallBold">{team.name}</Text>
                        </Team>
                      </div>
                    ))}
                  </Teams>
                  {!!errors.teams && (
                    <ValidationMessage>
                      {(errors.teams as unknown as FieldError).message}
                    </ValidationMessage>
                  )}
                </Larger>
              )}
              <Larger>
                <AllProductionTeams>
                  <div>
                    <Text $type="smallBold">
                      Assign to all production teams
                    </Text>
                  </div>
                  <div>
                    <StyledSwitch
                      name="assign_to_all_production_teams"
                      control={control}
                    />
                  </div>
                </AllProductionTeams>
              </Larger>
              {!assignToAllProductionTeams && (
                <Larger>
                  <StyledText $type="smallBold">Production teams</StyledText>
                  <Text $color="gray" $type="smallMedium">
                    Select multiple teams for one user.
                  </Text>
                  <Teams>
                    {productionTeams.map((team, index) => (
                      <div key={index}>
                        <Checkbox
                          type="checkbox"
                          id={`production-team-${index}-checkbox`}
                          checked={selectedProductionTeams.includes(team)}
                          onChange={(e) => {
                            setValue(
                              'production_teams',
                              e.target.checked
                                ? [...selectedProductionTeams, team]
                                : selectedProductionTeams.filter(
                                    (t) => t !== team,
                                  ),
                            );
                          }}
                        />
                        <Team htmlFor={`production-team-${index}-checkbox`}>
                          <TeamInitials as="span" $type="smallBold">
                            {getInitialsFromString(team)}
                          </TeamInitials>
                          <Text $type="smallBold">{team}</Text>
                        </Team>
                      </div>
                    ))}
                  </Teams>
                  {!!errors.production_teams && (
                    <ValidationMessage>
                      {
                        (errors.production_teams as unknown as FieldError)
                          .message
                      }
                    </ValidationMessage>
                  )}
                </Larger>
              )}
              {showPermissionsField && rolePermissionNames.length > 0 && (
                <Larger>
                  <StyledText $type="smallBold">Role permissions</StyledText>
                  <Text $color="gray" $type="smallMedium">
                    Default permissions included with the {role} role
                  </Text>
                  <Teams>
                    {permissionsList
                      ?.filter((p) => rolePermissionNames.includes(p.name))
                      .map((permission) => (
                        <div key={permission.id}>
                          <Checkbox
                            type="checkbox"
                            id={`role-perm-${permission.id}-checkbox`}
                            checked
                            disabled
                          />
                          <Permission
                            htmlFor={`role-perm-${permission.id}-checkbox`}
                          >
                            <Text $type="smallBold">{permission.name}</Text>
                          </Permission>
                        </div>
                      ))}
                  </Teams>
                </Larger>
              )}
              {showPermissionsField && (
                <Larger>
                  <StyledText $type="smallBold">
                    Additional permissions
                  </StyledText>
                  <Text $color="gray" $type="smallMedium">
                    Give user additional permissions to perform certain actions
                  </Text>
                  <Teams>
                    {permissionsList
                      ?.filter((p) => !rolePermissionNames.includes(p.name))
                      .map((permission) => (
                        <div key={permission.id}>
                          <Checkbox
                            type="checkbox"
                            id={`perm-${permission.id}-checkbox`}
                            checked={selectedPermissions.includes(
                              permission.name,
                            )}
                            onChange={(e) => {
                              const name = permission.name;
                              setValue(
                                'permissions',
                                e.target.checked
                                  ? [...selectedPermissions, name]
                                  : selectedPermissions.filter(
                                      (p) => p !== name,
                                    ),
                              );
                            }}
                          />
                          <Permission
                            htmlFor={`perm-${permission.id}-checkbox`}
                          >
                            <Text $type="smallBold">{permission.name}</Text>
                          </Permission>
                        </div>
                      ))}
                  </Teams>
                  {!!errors.permissions && (
                    <ValidationMessage>
                      {(errors.permissions as unknown as FieldError).message}
                    </ValidationMessage>
                  )}
                </Larger>
              )}
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {user ? 'Update' : 'Invite'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default UserModal;
